import {useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  UserCheck,
  UserX,
  CheckCircle,
  XCircle,
  Trash2,
  AlertTriangle,
  Search,
  UserPlus,
  RefreshCw,
  Pencil,
  Wallet,
  Users,
  CalendarPlus,
  CalendarDays,
  CalendarRange,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import PageTitle from '../../components/PageTitle';
import { formatPlanLabel, formatTierLabel } from '../../constants/subscriptionPlans';

interface User {
  id: string;
  email: string;
  role: 'user' | 'admin';
  create_at: string;
  last_login_at: string | null;
  balance?: number;
  emailVerification?: { verified?: boolean };
  subscription?: {
    status: string;
    plan?: string;
    tier?: string;
    activatedAt?: string;
    expiresAt?: string;
  };
}

interface UserStats {
  totalUsers: number;
  today: number;
  yesterday: number;
  last7Days: number;
  last30Days: number;
}

const TIER_COLORS: Record<string, string> = {
  free: 'bg-muted text-foreground',
  max: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  ultra: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

const PLAN_DURATIONS: Record<string, number> = {
  monthly: 30,
  quarterly: 90,
  yearly: 365,
};

function DashboardUsers() {
  const { t } = useTranslation('dashboard');
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [creatingRandomUser, setCreatingRandomUser] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ tier: 'free', plan: '', expiresAt: '' });
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subUser, setSubUser] = useState<User | null>(null);
  const [subForm, setSubForm] = useState({ tier: 'free', plan: '', expiresAt: '' });
  const [savingSub, setSavingSub] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceUser, setBalanceUser] = useState<User | null>(null);
  const [balanceInput, setBalanceInput] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, roleFilter]);

  useEffect(() => {
    loadUserStats();
  }, []);

  const loadUserStats = async () => {
    try {
      const res = await axios.get('/api/admin/users/stats');
      setUserStats(res.data);
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter !== 'all' && { role: roleFilter }),
      });

      const response = await axios.get(`/api/admin/users?${params}`);

      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error(t('users.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'user' | 'admin') => {
    try {
      await axios.put(`/api/admin/users/${userId}/role`, { role: newRole });
      toast.success(t('users.role_updated', { role: newRole }));
      loadUsers();
    } catch (error) {
      console.error('Failed to update user role:', error);
      toast.error(t('users.role_update_failed'));
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      await axios.delete(`/api/admin/users/${userToDelete.id}`);
      toast.success(t('users.delete_success'));
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(t('users.delete_failed'));
    } finally {
      setDeletingUser(false);
    }
  };

  const openSubModal = (user: User) => {
    setSubUser(user);
    const tier = user.subscription?.tier || 'free';
    const plan = user.subscription?.plan || '';
    const expiresAt = user.subscription?.expiresAt
      ? formatDate(user.subscription.expiresAt)
      : '';
    setSubForm({ tier, plan, expiresAt });
    setShowSubModal(true);
  };

  const handleSubSave = async () => {
    if (!subUser) return;
    setSavingSub(true);
    try {
      await axios.put(`/api/admin/users/${subUser.id}/subscription`, {
        tier: subForm.tier,
        plan: subForm.tier === 'free' ? undefined : subForm.plan || undefined,
        status: 'active',
        expiresAt: subForm.tier === 'free' ? undefined : subForm.expiresAt || undefined,
      });
      toast.success(t('users.sub_updated'));
      setShowSubModal(false);
      setSubUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update subscription:', error);
      toast.error(error.response?.data?.error || t('users.sub_update_failed'));
    } finally {
      setSavingSub(false);
    }
  };

  const handleTierChange = (tier: string) => {
    if (tier === 'free') {
      setSubForm({ tier, plan: '', expiresAt: '' });
    } else {
      const plan = subForm.plan || 'monthly';
      const days = PLAN_DURATIONS[plan] || 30;
      const expires = new Date();
      expires.setDate(expires.getDate() + days);
      setSubForm({ tier, plan, expiresAt: formatDate(expires) });
    }
  };

  const handlePlanChange = (plan: string) => {
    const days = PLAN_DURATIONS[plan] || 30;
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    setSubForm(prev => ({ ...prev, plan, expiresAt: formatDate(expires) }));
  };

  const openDeleteModal = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const openBalanceModal = (user: User) => {
    setBalanceUser(user);
    setBalanceInput((user.balance ?? 0).toFixed(2));
    setShowBalanceModal(true);
  };

  const handleBalanceSave = async () => {
    if (!balanceUser) return;
    const next = Number(balanceInput);
    if (!Number.isFinite(next) || next < 0) {
      toast.error(t('users.balance_invalid'));
      return;
    }
    setSavingBalance(true);
    try {
      await axios.put(`/api/admin/users/${balanceUser.id}/balance`, { balance: next });
      toast.success(t('users.balance_updated'));
      setShowBalanceModal(false);
      setBalanceUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update balance:', error);
      toast.error(error.response?.data?.error || t('users.balance_update_failed'));
    } finally {
      setSavingBalance(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({ tier: 'free', plan: '', expiresAt: '' });
    setShowCreateModal(true);
  };

  const handleCreateTierChange = (tier: string) => {
    if (tier === 'free') {
      setCreateForm({ tier, plan: '', expiresAt: '' });
    } else {
      const plan = createForm.plan || 'monthly';
      const days = PLAN_DURATIONS[plan] || 30;
      const expires = new Date();
      expires.setDate(expires.getDate() + days);
      setCreateForm({ tier, plan, expiresAt: formatDate(expires) });
    }
  };

  const handleCreatePlanChange = (plan: string) => {
    const days = PLAN_DURATIONS[plan] || 30;
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    setCreateForm(prev => ({ ...prev, plan, expiresAt: formatDate(expires) }));
  };

  const createRandomUser = async () => {
    setCreatingRandomUser(true);
    try {
      const body: any = {};
      if (createForm.tier !== 'free') {
        body.subscription = {
          tier: createForm.tier,
          plan: createForm.plan || undefined,
          expiresAt: createForm.expiresAt || undefined,
        };
      }
      const response = await axios.post('/api/admin/users/create-random', body);
      toast.success(t('users.create_success', { email: response.data.user.email }));
      setShowCreateModal(false);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to create random user:', error);
      toast.error(error.response?.data?.error || t('users.create_failed'));
    } finally {
      setCreatingRandomUser(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="users.title" namespace="dashboard" descriptionKey="users.subtitle" />
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">
              {t('users.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('users.subtitle')}
            </p>
          </div>

          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            {t('users.create_random')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {userStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.stats_total')}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{formatCompactNumber(userStats.totalUsers)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                <CalendarPlus className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.stats_today')}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{formatCompactNumber(userStats.today)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                <CalendarDays className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.stats_yesterday')}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{formatCompactNumber(userStats.yesterday)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-violet-50 dark:bg-violet-950/30">
                <CalendarRange className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.stats_7days')}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{formatCompactNumber(userStats.last7Days)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/30">
                <CalendarRange className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('users.stats_30days')}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{formatCompactNumber(userStats.last30Days)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('users.search_placeholder')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when search changes
                }}
                className="pl-9 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent w-full"
              />
            </div>
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as any);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">{t('users.all_roles')}</option>
            <option value="user">{t('users.users_filter')}</option>
            <option value="admin">{t('users.admins_filter')}</option>
          </select>

        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_plan')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_expires')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_email_verified')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_joined')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_balance')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_last_login')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('users.col_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-sm font-medium text-foreground">
                            {(user.email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-foreground">
                            {user.email}
                          </div>
                          {user.role === 'admin' && (
                            <Shield className="h-4 w-4 text-primary" />
                          )}
                          {user.id === currentUser?.id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {t('users.you_badge')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${TIER_COLORS[user.subscription?.tier || 'free'] || TIER_COLORS.free}`}>
                      {formatTierLabel(user.subscription?.tier)}
                      {user.subscription?.plan && user.subscription.tier !== 'free' ? ` / ${formatPlanLabel(user.subscription.plan)}` : ''}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {user.subscription?.expiresAt
                      ? (() => {
                          const exp = new Date(user.subscription!.expiresAt!);
                          const now = new Date();
                          const isExpired = exp < now;
                          return (
                            <span className={isExpired ? 'text-red-500 dark:text-red-400' : ''}>
                              {formatDate(exp)}
                              {isExpired && ` ${t('users.expired_label')}`}
                            </span>
                          );
                        })()
                      : <span className="text-muted-foreground">—</span>
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      user.emailVerification?.verified
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                        : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                    }`}>
                      {user.emailVerification?.verified ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                      {user.emailVerification?.verified ? t('common:verified') : t('common:unverified')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDateTime(user.create_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-medium">
                    ${(user.balance ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {user.last_login_at ? formatDateTime(user.last_login_at) : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {user.id === currentUser?.id ? (
                        // Current user - show disabled button
                        <button
                          disabled
                          className="text-muted-foreground cursor-not-allowed"
                          title={t('users.cannot_change_own')}
                        >
                          {user.role === 'admin' ? <Shield className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                      ) : (
                        // Other users - allow role changes between user and admin
                        <button
                          onClick={() => updateUserRole(user.id, user.role === 'admin' ? 'user' : 'admin')}
                          className={`${
                            user.role === 'admin'
                              ? 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300'
                              : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                          }`}
                          title={user.role === 'admin' ? t('users.demote_to_user') : t('users.promote_to_admin')}
                        >
                          {user.role === 'admin' ? <UserX className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                        </button>
                      )}
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => openSubModal(user)}
                          className="inline-flex items-center p-1 rounded-md text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors"
                          title={t('users.edit_subscription')}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openBalanceModal(user)}
                        className="inline-flex items-center p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:text-emerald-300 dark:hover:bg-emerald-900/20 transition-colors"
                        title={t('users.edit_balance')}
                      >
                        <Wallet className="h-4 w-4" />
                      </button>
                      {user.role !== 'admin' && user.id !== currentUser?.id && (
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="inline-flex items-center p-1 rounded-md text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors"
                          title={t('users.delete_user')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <div className="text-sm text-foreground">
            {t('common:page_of', { current: currentPage, total: totalPages })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
            >
              {t('common:previous')}
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
            >
              {t('common:next')}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => e.key === 'Escape' && setShowDeleteModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card rounded-lg shadow-lg border border-border max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('users.delete_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('users.delete_warning')}
                  </p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-foreground">{t('users.email_label')}</span>
                    <span className="text-sm text-foreground">{userToDelete.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-foreground">{t('users.role_label')}</span>
                    <span className="text-sm text-foreground capitalize">{userToDelete.role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-foreground">{t('users.joined_label')}</span>
                    <span className="text-sm text-foreground">
                      {formatDateTime(userToDelete.create_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                  disabled={deletingUser}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deletingUser}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {deletingUser ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('common:deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {t('users.delete_user')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Random User Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => e.key === 'Escape' && setShowCreateModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card rounded-lg shadow-lg border border-border max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('users.create_random_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('users.create_random_desc')}
                  </p>
                </div>
              </div>

              <div className="mb-5 p-3 bg-muted/50 rounded-lg text-sm">
                <div className="space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('users.email_label')}</span>
                    <span className="font-mono text-foreground">{t('users.email_example')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('common:password')}:</span>
                    <span className="font-mono text-foreground">{t('users.password_same')}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('users.initial_subscription')}</label>
                  <p className="text-xs text-muted-foreground mb-2">{t('users.initial_sub_desc')}</p>
                  <select
                    value={createForm.tier}
                    onChange={e => handleCreateTierChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="free">{formatTierLabel('free')}</option>
                    <option value="plus">{formatTierLabel('plus')}</option>
                    <option value="max">{formatTierLabel('max')}</option>
                    <option value="ultra">{formatTierLabel('ultra')}</option>
                  </select>
                </div>

                {createForm.tier !== 'free' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('users.plan_duration')}</label>
                    <select
                      value={createForm.plan}
                      onChange={e => handleCreatePlanChange(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="monthly">{formatPlanLabel('monthly')}</option>
                      <option value="quarterly">{formatPlanLabel('quarterly')}</option>
                      <option value="yearly">{formatPlanLabel('yearly')}</option>
                    </select>
                  </div>
                )}

                {createForm.tier !== 'free' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('users.expires_at')}</label>
                    <input
                      type="text"
                      placeholder={t('common:date_format_ymd')}
                      value={createForm.expiresAt}
                      onChange={e => setCreateForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                  disabled={creatingRandomUser}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={createRandomUser}
                  disabled={creatingRandomUser}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {creatingRandomUser ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('common:creating')}
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      {t('users.create_user')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Edit Modal */}
      {showSubModal && subUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => e.key === 'Escape' && setShowSubModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card rounded-lg shadow-lg border border-border max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {t('users.edit_sub_title')}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">{subUser.email}</p>

              <div className="space-y-4">
                {/* Tier */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('users.tier_label')}</label>
                  <select
                    value={subForm.tier}
                    onChange={e => handleTierChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="free">{formatTierLabel('free')}</option>
                    <option value="plus">{formatTierLabel('plus')}</option>
                    <option value="max">{formatTierLabel('max')}</option>
                    <option value="ultra">{formatTierLabel('ultra')}</option>
                  </select>
                </div>

                {/* Plan duration — hidden for free */}
                {subForm.tier !== 'free' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('users.plan_duration')}</label>
                    <select
                      value={subForm.plan}
                      onChange={e => handlePlanChange(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="monthly">{formatPlanLabel('monthly')}</option>
                      <option value="quarterly">{formatPlanLabel('quarterly')}</option>
                      <option value="yearly">{formatPlanLabel('yearly')}</option>
                    </select>
                  </div>
                )}

                {/* Expiry date — hidden for free */}
                {subForm.tier !== 'free' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('users.expires_at')}</label>
                    <input
                      type="text"
                      placeholder={t('common:date_format_ymd')}
                      value={subForm.expiresAt}
                      onChange={e => setSubForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                {/* Current info */}
                <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>{t('users.current_tier')}</span>
                    <span className="font-medium">{formatTierLabel(subUser.subscription?.tier)}</span>
                  </div>
                  {subUser.subscription?.expiresAt && (
                    <div className="flex justify-between mt-1">
                      <span>{t('users.current_expiry')}</span>
                      <span className="font-medium">{formatDate(subUser.subscription.expiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                  disabled={savingSub}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleSubSave}
                  disabled={savingSub}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {savingSub ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('common:saving')}
                    </>
                  ) : (
                    t('common:save')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Edit Modal */}
      {showBalanceModal && balanceUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => e.key === 'Escape' && setShowBalanceModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card rounded-lg shadow-lg border border-border max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {t('users.edit_balance_title')}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">{balanceUser.email}</p>

              <div className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground flex justify-between">
                  <span>{t('users.current_balance')}</span>
                  <span className="font-medium text-foreground">${(balanceUser.balance ?? 0).toFixed(2)}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('users.new_balance')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={balanceInput}
                      onChange={e => setBalanceInput(e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t('users.balance_hint')}</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowBalanceModal(false)}
                  className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                  disabled={savingBalance}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleBalanceSave}
                  disabled={savingBalance}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {savingBalance ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('common:saving')}
                    </>
                  ) : (
                    t('common:save')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default DashboardUsers;
